/**
 * Phase 2 — Block editor for lesson content.
 * Supports: rich_text, video, file, embed blocks.
 * Drag-to-reorder via bulk reorder API.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiClient } from '@/lib/api';
import { LessonBlock, BlockType } from '@/lib/types';
import RichTextEditor from './RichTextEditor';

const BLOCK_LABELS: Record<BlockType, string> = {
    rich_text: '📝 Rich Text',
    video: '▶️ Video',
    file: '📎 File',
    embed: '🔗 Embed',
};

const BLOCK_BADGE: Record<BlockType, string> = {
    rich_text: 'Rich text',
    video: 'Video',
    file: 'File',
    embed: 'Embed',
};

const BLOCK_ICONS: Record<BlockType, string> = {
    rich_text: 'T',
    video: '▶',
    file: '📄',
    embed: '🔗',
};

function stripHtml(html: string): string {
    return html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/\s+/g, ' ')
        .trim();
}

function urlPreview(url: string): string {
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.replace(/^www\./, '');
        const path = parsed.pathname === '/' ? '' : parsed.pathname;
        return `${host}${path}`;
    } catch {
        return url;
    }
}

function fileNameFromUrl(url: string): string {
    try {
        const part = new URL(url).pathname.split('/').filter(Boolean).pop();
        return part ? decodeURIComponent(part) : url;
    } catch {
        return url.split('/').filter(Boolean).pop() || url;
    }
}

function snippet(text: string, max = 120): string {
    if (text.length <= max) return text;
    return `${text.slice(0, max).trimEnd()}…`;
}

function blockPreview(block: LessonBlock): string {
    const p = (block.payload || {}) as Record<string, unknown>;
    switch (block.block_type) {
        case 'rich_text': {
            const text = stripHtml(String(p.html || ''));
            return text ? snippet(text) : '(empty)';
        }
        case 'video': {
            const caption = String(p.caption || '').trim();
            const url = String(p.url || '').trim();
            if (caption) return caption;
            return url ? urlPreview(url) : '(empty)';
        }
        case 'file': {
            const name = String(p.filename || '').trim();
            const url = String(p.url || '').trim();
            if (name) return name;
            return url ? fileNameFromUrl(url) : '(empty)';
        }
        case 'embed': {
            const url = String(p.url || '').trim();
            return url ? urlPreview(url) : '(empty)';
        }
        default:
            return '(empty)';
    }
}

function SortableBlock({
    block,
    onEdit,
    onDelete,
}: {
    block: LessonBlock;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
    const preview = blockPreview(block);

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
            className="rounded-xl border border-slate-200 bg-white p-3 flex items-start gap-2.5"
        >
            <button
                type="button"
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 mt-0.5 shrink-0"
                title="Drag to reorder"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
            </button>
            <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center text-xs font-bold text-blue-600">
                {BLOCK_ICONS[block.block_type]}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    {BLOCK_BADGE[block.block_type]}
                </p>
                <p className={`mt-0.5 text-sm leading-snug line-clamp-2 ${preview === '(empty)' ? 'text-slate-400 italic' : 'text-slate-800'}`}>
                    {preview}
                </p>
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
                <button
                    type="button"
                    onClick={onEdit}
                    className="p-1.5 rounded-lg text-amber-700 hover:bg-amber-50 transition-colors"
                    title="Edit"
                    aria-label="Edit"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                    title="Delete"
                    aria-label="Delete"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

function BlockForm({
    block,
    onSave,
    onCancel,
    enableAutosave,
    onAutosaved,
    scrollOnMount,
}: {
    block?: Partial<LessonBlock>;
    onSave: (data: { block_type: BlockType; payload: Record<string, unknown> }) => void;
    onCancel: () => void;
    enableAutosave?: boolean;
    onAutosaved?: (payload: Record<string, unknown>) => void;
    scrollOnMount?: boolean;
}) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [blockType, setBlockType] = useState<BlockType>(block?.block_type || 'rich_text');
    const [payload, setPayload] = useState<Record<string, string>>(
        block?.payload ? Object.fromEntries(Object.entries(block.payload).map(([k, v]) => [k, String(v)])) : {}
    );
    const [autosaveLabel, setAutosaveLabel] = useState<string | null>(null);
    const snapshot = JSON.stringify(payload);
    const lastSavedRef = useRef(snapshot);
    const onAutosavedRef = useRef(onAutosaved);
    onAutosavedRef.current = onAutosaved;

    useEffect(() => {
        if (!scrollOnMount) return;
        wrapRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [scrollOnMount]);

    const setField = (key: string, value: string) => setPayload(p => ({ ...p, [key]: value }));

    useEffect(() => {
        if (!enableAutosave || !block?.id) return;
        if (snapshot === lastSavedRef.current) return;
        setAutosaveLabel('Saving…');
        const t = setTimeout(async () => {
            const res = await apiClient.updateBlock(block.id as number, { payload });
            if (res.success) {
                lastSavedRef.current = snapshot;
                setAutosaveLabel('Saved');
                onAutosavedRef.current?.(payload);
                setTimeout(() => setAutosaveLabel(null), 1500);
            } else {
                setAutosaveLabel('Save failed');
            }
        }, 800);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshot, enableAutosave, block?.id]);

    return (
        <div ref={wrapRef} className="rounded-xl border border-blue-100 bg-blue-50 p-4 space-y-3">
            <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Block Type</label>
                <select
                    value={blockType}
                    onChange={e => { setBlockType(e.target.value as BlockType); setPayload({}); }}
                    disabled={!!block?.id}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                >
                    {(Object.keys(BLOCK_LABELS) as BlockType[]).map(t => (
                        <option key={t} value={t}>{BLOCK_LABELS[t]}</option>
                    ))}
                </select>
            </div>

            {blockType === 'rich_text' && (
                <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Content</label>
                    <RichTextEditor
                        value={payload.html || ''}
                        onChange={html => setField('html', html)}
                        placeholder="Write the lesson content…"
                    />
                </div>
            )}

            {blockType === 'video' && (
                <>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Video URL *</label>
                        <input
                            type="url"
                            value={payload.url || ''}
                            onChange={e => setField('url', e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=…"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Caption</label>
                        <input
                            type="text"
                            value={payload.caption || ''}
                            onChange={e => setField('caption', e.target.value)}
                            placeholder="Optional caption…"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </>
            )}

            {blockType === 'file' && (
                <>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">File URL *</label>
                        <input
                            type="url"
                            value={payload.url || ''}
                            onChange={e => setField('url', e.target.value)}
                            placeholder="https://…/document.pdf"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Display Name</label>
                        <input
                            type="text"
                            value={payload.filename || ''}
                            onChange={e => setField('filename', e.target.value)}
                            placeholder="e.g. Week 1 Notes.pdf"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </>
            )}

            {blockType === 'embed' && (
                <>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Embed URL *</label>
                        <input
                            type="url"
                            value={payload.url || ''}
                            onChange={e => setField('url', e.target.value)}
                            placeholder="https://…"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Provider</label>
                        <input
                            type="text"
                            value={payload.provider || ''}
                            onChange={e => setField('provider', e.target.value)}
                            placeholder="e.g. Figma, Loom…"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </>
            )}

            <div className="flex items-center gap-2 pt-1">
                <button
                    onClick={() => onSave({ block_type: blockType, payload: payload as Record<string, unknown> })}
                    className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                >
                    Save Block
                </button>
                <button
                    onClick={onCancel}
                    className="rounded-lg border border-slate-200 px-4 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                    Cancel
                </button>
                {autosaveLabel && (
                    <span className="text-xs text-emerald-600 font-medium">{autosaveLabel}</span>
                )}
            </div>
        </div>
    );
}

export default function LessonBlockEditor({ lessonId }: { lessonId: number }) {
    const [blocks, setBlocks] = useState<LessonBlock[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingBlock, setEditingBlock] = useState<LessonBlock | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const load = useCallback(async () => {
        setLoading(true);
        const res = await apiClient.getBlocks(lessonId);
        if (res.success && res.data) {
            const items = Array.isArray(res.data) ? res.data : (res.data as any)?.results || [];
            setBlocks(items.sort((a: LessonBlock, b: LessonBlock) => a.order - b.order));
        }
        setLoading(false);
    }, [lessonId]);

    useEffect(() => { load(); }, [load]);

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = blocks.findIndex(b => b.id === active.id);
        const newIndex = blocks.findIndex(b => b.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const reordered = arrayMove(blocks, oldIndex, newIndex);
        setBlocks(reordered);
        await apiClient.reorderBlocks(reordered.map(b => b.id));
        load();
    };

    const handleSave = async (data: { block_type: BlockType; payload: Record<string, unknown> }) => {
        if (editingBlock) {
            const res = await apiClient.updateBlock(editingBlock.id, { payload: data.payload });
            if (!res.success) { setError('Failed to save block'); return; }
        } else {
            const res = await apiClient.createBlock({ lesson: lessonId, ...data });
            if (!res.success) { setError('Failed to create block'); return; }
        }
        setEditingBlock(null);
        setShowForm(false);
        load();
    };

    const handleDelete = async (block: LessonBlock) => {
        if (!confirm('Delete this block?')) return;
        const res = await apiClient.deleteBlock(block.id);
        if (res.success) load();
        else setError('Failed to delete block');
    };

    if (loading) {
        return <div className="py-6 flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>;
    }

    const closeForm = () => {
        setShowForm(false);
        setEditingBlock(null);
    };

    return (
        <div className="space-y-3">
            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                    {error}
                    <button className="ml-2 underline" onClick={() => setError(null)}>×</button>
                </div>
            )}

            {blocks.length === 0 && !showForm && (
                <div className="py-8 text-center text-xs text-slate-400 rounded-xl border-2 border-dashed border-slate-200">
                    No content blocks yet. Add your first block below.
                </div>
            )}

            {blocks.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                        items={blocks.filter(b => b.id !== editingBlock?.id).map(b => b.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-2">
                            {blocks.map(block => (
                                editingBlock?.id === block.id ? (
                                    <BlockForm
                                        key={block.id}
                                        block={editingBlock}
                                        enableAutosave
                                        scrollOnMount
                                        onAutosaved={(payload) => {
                                            setBlocks(prev => prev.map(b => b.id === block.id ? { ...b, payload } : b));
                                        }}
                                        onSave={handleSave}
                                        onCancel={closeForm}
                                    />
                                ) : (
                                    <SortableBlock
                                        key={block.id}
                                        block={block}
                                        onEdit={() => { setEditingBlock(block); setShowForm(false); }}
                                        onDelete={() => handleDelete(block)}
                                    />
                                )
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {showForm ? (
                <BlockForm
                    scrollOnMount
                    onSave={handleSave}
                    onCancel={closeForm}
                />
            ) : !editingBlock ? (
                <button
                    onClick={() => { setShowForm(true); setEditingBlock(null); }}
                    className="w-full rounded-xl border-2 border-dashed border-slate-200 py-2.5 text-xs font-medium text-slate-400 hover:border-blue-300 hover:text-blue-500 transition-colors"
                >
                    + Add Content Block
                </button>
            ) : null}
        </div>
    );
}
